import type {
  TokenResponse,
  GetMessagesResponse,
  SendMessageRequest,
  SendMessageResponse,
} from '../types';
import type { TokenManager } from './token';

/**
 * Default request timeout (30 seconds)
 */
const REQUEST_TIMEOUT = 30000;

/**
 * API client for EasyHelp Edge Functions
 * Does NOT use Supabase client - direct fetch calls
 */
export class WidgetApiClient {
  private apiUrl: string;
  private token: string | null = null;
  private tokenManager: TokenManager | null = null;
  private onAuthError?: () => void;

  constructor(apiUrl: string) {
    // Remove trailing slash
    this.apiUrl = apiUrl.replace(/\/$/, '');
  }

  /**
   * Set callback for auth errors (401/403)
   */
  setAuthErrorCallback(callback: () => void): void {
    this.onAuthError = callback;
  }

  /**
   * Set token manager for proactive token refresh before API calls
   */
  setTokenManager(tm: TokenManager): void {
    this.tokenManager = tm;
  }

  /**
   * Set the JWT token for authenticated requests
   */
  setToken(token: string | null): void {
    this.token = token;
  }

  /**
   * Get auth headers for requests
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  /**
   * Make a fetch request with timeout and error handling
   */
  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Proactively refresh token if expired or about to expire
    if (this.tokenManager) {
      const validToken = await this.tokenManager.ensureValidToken();
      if (validToken) {
        this.token = validToken;
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${this.apiUrl}${endpoint}`, {
        ...options,
        headers: {
          ...this.getHeaders(),
          ...(options.headers || {}),
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const text = await response.text();

      if (!response.ok) {
        // Try to parse error body for structured error info
        let errorMessage = text || `HTTP ${response.status}`;
        let errorCode = 'API_ERROR';

        try {
          const errorBody = JSON.parse(text);
          errorMessage = errorBody.error?.message || errorBody.message || errorMessage;
          errorCode = errorBody.error?.code || errorCode;
        } catch {
          // text is not JSON, use raw text as message
        }

        const error = new ApiError(errorMessage, response.status, errorCode);

        // Notify callback on auth errors to trigger token cleanup
        if (error.isAuthError() && this.onAuthError) {
          this.onAuthError();
        }

        throw error;
      }

      // Handle empty/no-content responses (204, etc.)
      if (!text || text.trim() === '') {
        return null as unknown as T;
      }

      // Parse JSON with error handling
      try {
        return JSON.parse(text) as T;
      } catch {
        throw new ApiError('Invalid JSON response from server', 502, 'INVALID_JSON');
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408, 'TIMEOUT');
      }

      if (error instanceof TypeError) {
        // Network error
        throw new ApiError('Network error - please check your connection', 0, 'NETWORK_ERROR');
      }

      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error',
        500,
        'UNKNOWN'
      );
    }
  }

  /**
   * Request a widget token from Easy.School backend
   * Note: This should be called through the host app's backend, not directly
   */
  async getToken(params: {
    apiKey: string;
    tenantId: string;
    customerId: string;
    customerName?: string;
    customerEmail?: string;
    conversationId?: string;
  }): Promise<TokenResponse> {
    return this.fetch<TokenResponse>('/widget-token', {
      method: 'POST',
      headers: {
        'X-API-Key': params.apiKey,
      },
      body: JSON.stringify({
        tenant_id: params.tenantId,
        customer_external_id: params.customerId,
        customer_name: params.customerName,
        customer_email: params.customerEmail,
        conversation_id: params.conversationId,
      }),
    });
  }

  /**
   * Get messages for a conversation
   */
  async getMessages(params: {
    conversationId: string;
    limit?: number;
    cursor?: string;
    since?: string;
  }): Promise<GetMessagesResponse> {
    const searchParams = new URLSearchParams({
      conversation_id: params.conversationId,
      limit: String(params.limit || 50),
    });

    if (params.cursor) {
      searchParams.set('cursor', params.cursor);
    }

    if (params.since) {
      searchParams.set('since', params.since);
    }

    return this.fetch<GetMessagesResponse>(
      `/get-messages?${searchParams.toString()}`,
      { method: 'GET' }
    );
  }

  /**
   * Send a message to a conversation
   */
  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    return this.fetch<SendMessageResponse>('/send-message', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if error is due to network/offline
   */
  isNetworkError(): boolean {
    return this.status === 0 || this.code === 'NETWORK_ERROR';
  }

  /**
   * Check if error is auth-related
   */
  isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /**
   * Check if error is rate limit
   */
  isRateLimitError(): boolean {
    return this.status === 429;
  }
}
